import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe2, RefreshCw, Sparkles } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { ApiError, api, background } from "../../api/client";
import type { CountrySection, CountrySheet } from "../../api/types";
import { Waiting } from "../../components/Waiting";
import { attentionSelector, designate, STALE_REGENERATE } from "../../components/attention";
import { Banner, Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { GenerationVeil, GenerationWait, jobProgress } from "../../components/GenerationVeil";
import { Presence } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { ViewHeader } from "../../components/ViewHeader";
import { k, t, tServer } from "../../i18n/i18n";
import { ZoneWaitBanner } from "../../map/zones/ZoneWaitCallout";
import { briefingZoneBanner } from "../../map/zones/zoneRefusal";
import { plural } from "../../plural";
import { designateDossierReadOnly, useDossierAccess } from "../../sharing/access";
import { licenceStore } from "../../state/licenceScope";
import { useUi } from "../../state/ui";
import { clearLocalCopy, leaveWith, readLocalCopy } from "../../state/unsavedDrafts";
import { FRAME } from "../briefing/BriefingView";
import { AiChip, ProgressStrip, type ProgressTexts } from "../briefing/DocumentChrome";
import { resumeKeyFor, writingInProgress } from "../briefing/documentJob";
import { type DocFigure, type DocNode, type DocTypography, PagedBriefing, type PagedSection, type SectionVariant } from "../briefing/PagedBriefing";
import { DossierEmptyState } from "../DossierEmptyState";
import { CompareDialog } from "../order/CompareDialog";
import { ExportMenu } from "../order/ExportMenu";
import { TranslationBanner, translationInterval } from "../translation/TranslationBanner";
import { PendingMissionDraftBanner } from "../PendingMissionDraftBanner";
import { type CountryJobBody, countrySheetKey, useCountryJob } from "./countryJob";
import { COUNTRY_WRITE_TARGET, sectionsToWrite } from "./countrySheet";

/** Fiche pays du dossier d'exercice (demande d'Alexis du 11/10/2026) : le pays de l'ennemi joué (TITAN, MERCURE…),
 *  celui où opèrent DEIMOS et TANTALE, ou un adversaire générique face à une force non française, sur un territoire
 *  inventé autour de la zone d'exercice (camp ou terrain libre), sur sa vraie géographie. Generex compose la fiche (faits, chiffres, dirigeants, carte), propre au
 *  dossier ; l'IA locale rédige ses rubriques de texte sans rien ajouter. Feuilles A4 au cadre de la mise en ambiance
 *  et de l'OPO (trois au plus), disposition dense de son PDF (server/generex_api/country_layout.py) : carte du pays
 *  et en-tête côte à côte, chiffres en deux colonnes, carte des commandements à côté de leur liste ; rubriques
 *  modifiables une à une, export PDF, Word, OpenDocument ou texte. */

/** Aide des formats d'export propre à la fiche pays (liste commune : order/ExportMenu.tsx). */
const EXPORT_HINTS = {
  pdf: k("Au cadre des pages de la mise en ambiance et de l'OPO, cartes comprises"),
  docx: k("Document modifiable, cartes comprises"),
};

/** Lien vers les Données initiales, désigné quand le dossier est incomplet (R12). */
const COMPLETE_TARGET = "country-complete";

/** Fiche d'aide ouverte par le « ? » de la vue. */
const HELP_TOPIC = "dossier.fiche-pays";

/** Copie locale d'une rubrique en cours de modification : `generex-country-draft:<dossier>:<rubrique>`. */
const COUNTRY_DRAFT_PREFIX = "generex-country-draft:";

/** Manière de chaque sorte de rubrique dans les feuilles : chiffres et dirigeants en lignes « Libellé : valeur ». */
const VARIANTS: Record<CountrySection["kind"], SectionVariant> = {
  facts: "facts",
  leaders: "facts",
  list: "list",
  text: "body",
};

/** Rubrique telle que les feuilles l'affichent : rubrique de texte comme un paragraphe de l'IA (en attente pendant
 *  la rédaction) ; « à revoir » passe par sa pastille, le texte n'est jamais estompé. */
function pagedSection(section: CountrySection, running: boolean): PagedSection {
  return {
    id: section.id,
    title: section.title,
    kind: section.ai ? "llm" : "template",
    text: section.text,
    edited: section.edited,
    status: running && section.queued ? "pending" : section.status,
    fallback_reason: section.fallback_reason,
    stale: false,
    withheld: section.withheld,
    model: section.model,
    generated_at: section.generated_at,
    translating: section.translating,
  };
}


type Confirm = null | { kind: "reseed" } | { kind: "section"; id: string };

export function CountryView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level);
  // Tant que la liste des dossiers n'est pas chargée, on ne sait pas encore : ni bandeau « Dossier incomplet » ni
  // blocage, seulement les actions désactivées.
  const known = Boolean(exercise);
  const incomplete = known && !complete;
  const job = useCountryJob((s) => (s.job && s.job.exerciseId === exerciseId ? s.job : null));
  const start = useCountryJob((s) => s.start);
  const stop = useCountryJob((s) => s.stop);
  const dismiss = useCountryJob((s) => s.dismiss);
  const query = useQuery({
    queryKey: countrySheetKey(exerciseId),
    queryFn: () => background(() => api.countrySheet(exerciseId as string)),
    enabled: Boolean(exerciseId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    // Rédaction en cours ailleurs (autre poste, ou celle d'avant un rechargement qui s'achève) : relue toutes les
    // 10 s, ses rubriques s'affichent à mesure et une rédaction interrompue est reprise dès son échéance.
    // Préparation d'avance : relue plus souvent, ses rubriques s'affichent à mesure.
    // Lue dans l'autre langue que sa langue de rédaction (docs/13) : relue toutes les 2,5 s tant qu'une traduction
    // attend.
    refetchInterval: (q) =>
      translationInterval(
        Boolean(q.state.data?.translating?.length),
        q.state.data?.writing && !useCountryJob.getState().job?.running ? (q.state.data.preparing ? 4_000 : 10_000) : false,
      ),
  });
  const sheet = query.data;
  const { data: ai } = useQuery({ queryKey: ["ai-status"], queryFn: () => background(api.aiStatus), refetchInterval: 30_000 });
  // Thème tactique de l'OPO N° 1, sinon le nom du dossier : la ligne de tête des pages, comme dans le PDF.
  const { data: order } = useQuery({
    queryKey: ["order", exerciseId, 1],
    queryFn: () => api.order(exerciseId as string, 1),
    enabled: Boolean(exerciseId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  // Terrain d'une zone libre pas prêt : l'indisponibilité de la fiche est un refus de zone ; un seul bandeau
  // d'attente, celui de l'OPO périmé s'il existe.
  const { message: zoneStale, hideUnavailable: zoneUnavailable } = briefingZoneBanner(order?.stale, query.data?.unavailable);
  const themeValue = String(order?.fields?.themeTactique ?? "").trim() || exercise?.name || "";
  const running = Boolean(job?.running);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [compare, setCompare] = useState<string | null>(null);

  // Rédiger ou régénérer ferme la rubrique en cours de modification : la question commune d'abord (M4).
  const dossierLocked = useDossierAccess().readOnly;
  const generate = (body: CountryJobBody) => void leaveWith(() => generateNow(body));
  const generateNow = (body: CountryJobBody) => {
    if (!exerciseId || running) return;
    // Dossier en lecture seule pour ce compte (docs/17 §3.2) : la rédaction désigne le bandeau de lecture seule.
    if (dossierLocked) {
      designateDossierReadOnly();
      return;
    }
    setConfirm(null);
    setEditing(null);
    void start(exerciseId, body, queryClient);
  };

  // Rédaction interrompue (rechargement, mise en ligne, redémarrage du serveur) : les rubriques restées en attente
  // sont rédigées d'office à la réouverture ; un seul poste reprend. Rédaction arrêtée sur ce poste : jamais relancée.
  const interrupted = sheet?.interrupted ?? [];
  const resumeKey = resumeKeyFor(exerciseId, sheet, { complete, running, stopped: Boolean(job?.stopped) });
  const resumed = useRef<string | null>(null);
  useEffect(() => {
    if (!resumeKey || !exerciseId || resumed.current === resumeKey) return;
    resumed.current = resumeKey;
    notify({
      text: t("Rédaction interrompue : reprise en cours"),
      detail: t("Seules les rubriques restées en attente sont rédigées ; les autres ne changent pas."),
      tone: "info",
    });
    void start(exerciseId, { sections: interrupted, resume: true }, queryClient);
  }, [resumeKey]);

  // Rubrique d'une session précédente non enregistrée : elle se rouvre en modification, une fois par dossier.
  const copyPrefix = `${COUNTRY_DRAFT_PREFIX}${exerciseId ?? ""}:`;
  const restoredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!sheet || !exerciseId || running || restoredFor.current === copyPrefix) return;
    restoredFor.current = copyPrefix;
    for (const key of licenceStore.keys()) {
      if (!key.startsWith(copyPrefix)) continue;
      const section = sheet.sections.find((s) => s.id === key.slice(copyPrefix.length));
      const copy = readLocalCopy<string>(key);
      if (!section || !copy || typeof copy.value !== "string" || copy.value.trim() === section.text.trim()) {
        clearLocalCopy(key);
        continue;
      }
      setEditing(section.id);
      break;
    }
  }, [sheet, exerciseId, running, copyPrefix]);

  const apply = (next: CountrySheet) => queryClient.setQueryData(countrySheetKey(exerciseId), next);
  // Échec d'une action : avis en ton d'erreur (les bandeaux sont réservés aux états durables).
  const fail = (err: unknown) =>
    notify({ text: err instanceof Error && err.message ? err.message : t("Opération impossible"), tone: "error" });
  // Dossier incomplet (carte ou niveau manquant) : le lien vers les Données initiales est l'action attendue.
  const completeFirst = () => {
    designate(attentionSelector(COMPLETE_TARGET));
    notify({ text: t("Complétez d'abord les Données initiales : carte et niveau du dossier."), tone: "info" });
  };
  const edit = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.editCountrySheet(exerciseId as string, sheet?.version ?? 0, { [id]: text }),
    onSuccess: (next) => {
      apply(next);
      setEditing(null);
      setCompare(null);
    },
    onError: async (err) => {
      fail(err);
      await queryClient.invalidateQueries({ queryKey: countrySheetKey(exerciseId) });
    },
  });
  const reset = useMutation({
    mutationFn: (id: string) => api.resetCountrySection(exerciseId as string, id),
    onSuccess: (next) => {
      apply(next);
      setCompare(null);
    },
    onError: fail,
  });

  const notFound = query.error instanceof ApiError && query.error.status === 404;
  const languageName = (code: string) => (code === "en" ? t("anglais") : t("français"));

  // Dossier absent, fiche à composer, chargement : en-tête de la vue et état vide dessous (R13) ; la fiche composée
  // prend leur place sous le même en-tête, le bandeau de la rédaction suivant d'un bout à l'autre.
  const empty = Boolean(exerciseId) && !sheet && !running && (notFound || !query.isLoading);
  // « Rédiger la fiche pays » (et « Reprendre ») : les rubriques de texte que l'IA n'a pas encore rédigées, sinon
  // toutes celles qui ne sont pas modifiées à la main.
  const writeAll = () => {
    const pending = sheet ? sectionsToWrite(sheet.sections) : [];
    // Paragraphes inchangés depuis leur dernière rédaction repris tels quels (seuls ceux qui ont changé sont
    // redemandés à l'IA) ; « Régénérer » une rubrique, lui, la fait toujours rédiger de nouveau.
    generate(pending.length ? { sections: pending, reuse: true } : { reuse: true });
  };
  const strip = (
    <Presence show={Boolean(job)}>
      {job && <ProgressStrip job={job} texts={progressTexts()} onStop={stop} onRetry={writeAll} onDismiss={dismiss} />}
    </Presence>
  );
  if (!exerciseId || !sheet) {
    return (
      <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
        <ViewHeader view="country" helpTopic={HELP_TOPIC} context={exercise?.name} actions={exerciseId ? <CountryAiChip ai={ai} /> : null} />
        {strip}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!exerciseId ? (
            <DossierEmptyState view="country" kind="none">
              {t("Ouvrez ou créez un dossier : la fiche pays se compose à partir de ses Données initiales et de son ennemi.")}
            </DossierEmptyState>
          ) : empty ? (
            <CountryEmpty
              complete={complete}
              known={known}
              aiAvailable={ai?.available}
              error={job?.error ?? (query.error && !notFound ? (query.error as Error).message : null)}
              onGenerate={() => generate({})}
              onComplete={() => setView("mission")}
              onBlocked={completeFirst}
            />
          ) : running ? (
            <GenerationWait note={t("Composition de la fiche pays : faits, chiffres et cartes.")} />
          ) : (
            <EmptyState variant="loading" title={t("Chargement de la fiche pays…")} />
          )}
        </div>
      </div>
    );
  }

  const byId = new Map(sheet.sections.map((s) => [s.id, { ...s, ...(job?.sections[s.id] ?? {}) } as CountrySection]));
  const sections = Object.fromEntries([...byId.values()].map((s) => [s.id, pagedSection(s, running)]));
  const editedCount = sheet.sections.filter((s) => s.edited).length;
  const staleCount = sheet.sections.filter((s) => s.stale).length;
  const toWrite = sectionsToWrite(sheet.sections);
  const lang = sheet.language === "en" ? "en" : "fr";
  // Disposition du PDF (country_layout.py) : typographie du niveau, taille des cartes, rubriques d'en-tête à droite de
  // la carte du pays ; chiffres en deux colonnes ; carte des commandements à droite de leur liste.
  const layout = sheet.layout;
  const typography: DocTypography | undefined = layout
    ? {
        body: layout.body,
        leading: layout.leading,
        after: layout.after,
        item: layout.item,
        heading: layout.heading,
        before: layout.before,
        under: layout.under,
      }
    : undefined;
  const figure = (
    key: "map" | "commands",
    info: CountrySheet["map"],
    size: [number | null, number | null],
    side: DocFigure["side"],
    maxHeight: number,
  ): DocFigure | undefined =>
    info && size[0] && size[1]
      ? {
          key,
          src: api.countrySheetMapUrl(exerciseId, key === "map" ? "country" : "commands", info.v),
          alt: key === "map" ? t("Carte de {pays}", { pays: sheet.country }) : t("Carte des commandements de {pays}", { pays: sheet.country }),
          width: size[0],
          height: size[1],
          side,
          maxHeight,
        }
      : undefined;
  const countryMap = figure("map", sheet.map, [layout?.map_width ?? null, layout?.map_height ?? null], "left", 300);
  const commandsMap = figure("commands", sheet.commands_map, [layout?.commands_width ?? null, layout?.commands_height ?? null], "right", 260);
  const header = countryMap ? (layout?.header ?? 0) : 0;
  // Document dans l'ordre du PDF : titre, date, carte du pays (informations générales et dirigeants à sa droite),
  // rubriques (carte des commandements sous l'intitulé de la sienne).
  const nodes: DocNode<string>[] = [
    { type: "title", key: "title", text: sheet.title },
    ...(sheet.date_label ? [{ type: "subtitle" as const, key: "date", text: sheet.date_label }] : []),
    ...(countryMap
      ? [
          header
            ? ({ type: "float", ...countryMap } as const)
            : ({ type: "image", ...countryMap, width: sheet.map?.width ?? 1, height: sheet.map?.height ?? 1, maxHeight: countryMap.height } as const),
        ]
      : []),
    ...sheet.sections.map(
      (s, i): DocNode<string> => ({
        type: "section",
        id: s.id,
        variant: VARIANTS[s.kind] ?? "body",
        columns: s.kind === "facts" || s.kind === "leaders" ? (i < header ? 1 : 2) : undefined,
        pinned: i < header,
        figure: s.id === "commands" ? commandsMap : undefined,
      }),
    ),
  ];

  // Voile de rédaction : de la demande à la dernière rubrique écrite, sur ce poste ou un autre (rechargement
  // compris).
  const veiled = writingInProgress(job, sheet.writing);
  const aiIds = sheet.sections.filter((s) => s.ai && !s.edited).map((s) => s.id);
  const progress = job?.running ? jobProgress(job, aiIds, tServer) : null;

  const writeSection = (id: string, reuse = false) =>
    byId.get(id)?.edited ? setConfirm({ kind: "section", id }) : generate({ sections: [id], reuse });
  const compared = compare ? byId.get(compare) : undefined;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="country"
        helpTopic={HELP_TOPIC}
        context={exercise?.name}
        badges={
          <>
            <Badge title={t("Fiche rédigée en {language}", { language: languageName(sheet.language) })}>
              {sheet.language === "en" ? "EN" : "FR"}
            </Badge>
            {editedCount > 0 && <Badge tone="accent">{plural(editedCount, t("rubrique modifiée"), t("rubriques modifiées"))}</Badge>}
            {staleCount > 0 && <Badge tone="warn">{plural(staleCount, t("rubrique à revoir"), t("rubriques à revoir"))}</Badge>}
          </>
        }
        actions={
          <>
            <CountryAiChip ai={ai} />
            <Button
              size="sm"
              variant={toWrite.length > 0 ? "primary" : "secondary"}
              data-attention={COUNTRY_WRITE_TARGET}
              onClick={writeAll}
              disabled={running || !known}
              blocked={incomplete}
              onBlocked={completeFirst}
              highlight={toWrite.length > 0 && !running && complete}
              title={
                toWrite.length > 0
                  ? t("L'IA rédige les rubriques de texte d'après les faits de Generex, sans rien ajouter")
                  : t("Rédiger à nouveau par l'IA les rubriques de texte non modifiées")
              }
            >
              <Sparkles size={14} /> {t("Rédiger la fiche pays")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirm({ kind: "reseed" })}
              disabled={running || !known}
              blocked={incomplete}
              onBlocked={completeFirst}
              title={t("Tirer une nouvelle fiche : autres noms, autres chiffres, autre carte")}
            >
              <RefreshCw size={14} /> {t("Régénérer")}
            </Button>
            <ExportMenu
              formats={["pdf", "docx", "odt", "txt"]}
              href={(fmt) => api.countrySheetExportUrl(exerciseId, fmt as "pdf" | "docx" | "odt" | "txt")}
              hints={EXPORT_HINTS}
              disabled={running || Boolean(sheet?.translating?.length)}
              variant={toWrite.length > 0 ? "secondary" : "primary"}
            />
          </>
        }
      />

      {strip}
      <Presence show={Boolean(sheet.writing) && !job}>
        {sheet.writing && !job && (
          <Banner tone="info">
            {sheet.preparing ? (
              <Waiting>{t("Generex prépare la fiche pays d'avance : les rubriques s'afficheront dès qu'elles seront prêtes…")}</Waiting>
            ) : (
              t("Rédaction en cours : les rubriques s'afficheront dès qu'elles seront prêtes.")
            )}
          </Banner>
        )}
      </Presence>
      <Presence show={incomplete}>
        {incomplete && (
          <Banner
            tone="warn"
            actions={
              <Button size="sm" variant="secondary" data-attention={COMPLETE_TARGET} onClick={() => setView("mission")}>
                {t("Compléter les Données initiales")}
              </Button>
            }
          >
            {t("Dossier incomplet : choisissez la carte et le niveau dans les Données initiales pour composer la fiche pays.")}
          </Banner>
        )}
      </Presence>
      {/* Fiche indisponible faute de terrain prêt (même refus que l'ordre) : ce bandeau d'attente, mené à l'action
          attendue de la zone ; un seul quand l'OPO est aussi périmé. */}
      <Presence show={Boolean(zoneStale)}>{zoneStale && <ZoneWaitBanner message={zoneStale} />}</Presence>
      <PendingMissionDraftBanner exercise={exercise} />
      <TranslationBanner
        exerciseId={exerciseId}
        translating={Boolean(sheet.translating?.length)}
        untranslated={Boolean(sheet.untranslated?.length)}
      />
      <Presence show={Boolean(sheet.unavailable) && !zoneUnavailable}>
        {sheet.unavailable && !zoneUnavailable && (
          <Banner
            tone="warn"
            actions={
              <Button size="sm" variant="secondary" onClick={() => setView("mission")}>
                {t("Données initiales")}
              </Button>
            }
          >
            {tServer(sheet.unavailable)} {t("Les rubriques restent telles qu'elles ont été composées.")}
          </Banner>
        )}
      </Presence>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <GenerationVeil
          active={veiled}
          progress={progress}
          count={(done, total) => t("Rubriques rédigées : {done} sur {total}", { done, total })}
          note={t("Les rubriques s'afficheront dès qu'elles seront prêtes.")}
        >
          <PagedBriefing<string>
            lang={lang}
            nodes={nodes}
            sections={sections}
            job={job}
            editing={editing}
            savingId={edit.isPending ? (edit.variables?.id ?? null) : null}
            busy={running}
            banner={FRAME[lang].banner}
            themeLine={FRAME[lang].theme.replace("{theme}", themeValue)}
            copyPrefix={copyPrefix}
            view="country"
            typography={typography}
            rubrics
            canRegenerate={(section) => section.kind === "llm"}
            editHint={(id) => {
              const kind = byId.get(id)?.kind;
              return kind === "facts" || kind === "leaders"
                ? t("annule · une ligne « Libellé : valeur » par entrée")
                : kind === "list"
                  ? t("annule · une ligne par élément")
                  : undefined;
            }}
            renderChips={(section, state) => {
              const country = byId.get(section.id);
              return country ? (
                <CountryChips
                  section={country}
                  writing={state.writing}
                  queued={state.queued}
                  busy={state.busy}
                  onReview={() => setCompare(country.id)}
                  onRegenerate={() => writeSection(country.id, true)}
                />
              ) : null;
            }}
            onEdit={(id) => id !== editing && leaveWith(() => setEditing(id))}
            onCancelEdit={() => setEditing(null)}
            onSave={(id, text) => edit.mutateAsync({ id, text })}
            onRegenerate={writeSection}
            onReset={(id) => reset.mutate(id)}
            onCopied={() => notify({ text: t("Rubrique copiée dans le presse-papiers."), tone: "ok" })}
          />
        </GenerationVeil>
      </div>

      <Presence show={confirm?.kind === "reseed"}>
        {confirm?.kind === "reseed" && (
          <ConfirmDialog
            title={t("Régénérer la fiche pays ?")}
            confirmLabel={t("Régénérer")}
            busy={running}
            busyLabel={t("Régénération…")}
            onCancel={() => setConfirm(null)}
            onConfirm={() => generate({ reseed: true })}
          >
            {t("Une nouvelle fiche remplace celle-ci : autres noms, autres chiffres et autre carte, rédigés de nouveau par l'IA.")}
            {editedCount === 1 && ` ${t("Votre rubrique modifiée sera perdue.")}`}
            {editedCount > 1 && ` ${t("Vos {n} rubriques modifiées seront perdues.", { n: editedCount })}`}
          </ConfirmDialog>
        )}
      </Presence>
      <Presence show={confirm?.kind === "section"}>
        {confirm?.kind === "section" && (
          <ConfirmDialog
            title={t("Rédiger à nouveau cette rubrique ?")}
            confirmLabel={t("Rédiger")}
            busy={running}
            busyLabel={t("Rédaction…")}
            onCancel={() => setConfirm(null)}
            onConfirm={() => generate({ sections: [confirm.id] })}
          >
            {t("La rubrique « {title} » a été modifiée à la main : sa nouvelle rédaction remplacera vos modifications.", {
              title: byId.get(confirm.id)?.title ?? "",
            })}
          </ConfirmDialog>
        )}
      </Presence>
      <Presence show={Boolean(compared)}>
        {compared && (
          <CompareDialog
            title={compared.title}
            mine={compared.text}
            generated={compared.generated ?? ""}
            busy={edit.isPending || reset.isPending}
            readOnly={dossierLocked}
            subtitle={t("Le texte de Generex a changé depuis votre modification (ennemi, ordre, langue du dossier…).")}
            onKeepMine={() => edit.mutate({ id: compared.id, text: compared.text })}
            onTakeGenerated={() => reset.mutate(compared.id)}
            onClose={() => setCompare(null)}
          />
        )}
      </Presence>
    </div>
  );
}

/** Textes du bandeau de la rédaction de la fiche pays. */
function progressTexts(): ProgressTexts {
  return {
    one: (duration) => t("Rubrique rédigée en {duration}", { duration }),
    some: (duration) => t("Rubriques rédigées en {duration}", { duration }),
    all: (duration) => t("Fiche pays rédigée en {duration}", { duration }),
    stopped: t("Rédaction arrêtée : les rubriques terminées sont enregistrées."),
  };
}

function CountryAiChip({ ai }: { ai?: { available?: boolean; source?: string; resuming?: boolean } }) {
  return (
    <AiChip
      available={ai?.available}
      // Fiche pays toujours rédigée par le modèle rapide, quel que soit le choix du poste (décision d'Alexis du
      // 21/10/2026) : le modèle annoncé est celui qui rédigera.
      source={ai?.source?.replace("(modèle avancé)", "(modèle Generex)").replace(" Generex avancé)", " Generex)")}
      resuming={ai?.resuming}
      offline={t("L'IA ne répond pas : les rubriques garderont le texte de Generex.")}
    />
  );
}

/** Pastilles d'une rubrique : modifiée, à régénérer (rédaction de l'IA qui ne suit plus le dossier : un clic la fait
 *  rédiger de nouveau), à revoir (modification : un clic la compare au texte d'origine à jour). */
function CountryChips({
  section,
  writing,
  queued,
  busy,
  onReview,
  onRegenerate,
}: {
  section: CountrySection;
  writing: boolean;
  queued: boolean;
  busy: boolean;
  onReview: () => void;
  onRegenerate: () => void;
}) {
  const chips: ReactNode[] = [];
  // Ni pastille « IA » ni pastille du texte de Generex (« À rédiger », texte gardé) : demande d'Alexis du
  // 12/10/2026 ; la rédaction en cours se lit sur la carte d'attente des feuilles.
  if (section.edited) chips.push(<Badge key="edited" tone="accent">{t("Modifié")}</Badge>);
  if (!writing && !queued && section.stale && !section.edited) {
    // Rédaction de l'IA gardée alors que le texte de Generex a changé (ennemi passé de l'autre côté, unités…) : un clic la
    // fait rédiger de nouveau (audit tactique du 15/10/2026, constat 3).
    chips.push(
      <button
        key="stale"
        type="button"
        data-attention={STALE_REGENERATE}
        onClick={onRegenerate}
        disabled={busy}
        title={t("Le dossier a changé depuis la rédaction (ennemi, ordre, unités…) : rédiger à nouveau")}
        className="inline-flex rounded-md transition-[filter] hover:brightness-95 disabled:opacity-40"
      >
        <Badge tone="warn" className="gap-1">
          <RefreshCw size={12} /> {t("À régénérer")}
        </Badge>
      </button>,
    );
  } else if (!writing && !queued && section.stale) {
    chips.push(
      <button
        key="stale"
        type="button"
        data-attention={STALE_REGENERATE}
        onClick={onReview}
        disabled={busy}
        title={t("Le texte de Generex a changé depuis votre modification : comparer")}
        className="inline-flex rounded-md transition-[filter] hover:brightness-95 disabled:opacity-40"
      >
        <Badge tone="warn" className="gap-1">
          <RefreshCw size={12} /> {t("À revoir")}
        </Badge>
      </button>,
    );
  }
  return chips.length ? <div className="flex shrink-0 items-center gap-1.5">{chips}</div> : null;
}

/** Fiche pays à composer : état vide de la vue (R13), avec ce que Generex et l'IA vont faire et une seule action
 *  principale. Dossier incomplet : « Rédiger la fiche pays » désigne le lien vers les Données initiales. */
function CountryEmpty({
  complete,
  known,
  aiAvailable,
  error,
  onGenerate,
  onComplete,
  onBlocked,
}: {
  complete: boolean;
  known: boolean;
  aiAvailable?: boolean;
  error: string | null;
  onGenerate: () => void;
  onComplete: () => void;
  onBlocked: () => void;
}) {
  const items: { title: string; text: string }[] = [
    {
      title: t("Faits, chiffres et carte"),
      text: t("Tirés par Generex pour ce dossier seul : un pays inventé sur la vraie géographie qui borde la zone d'exercice."),
    },
    {
      title: t("Rubriques rédigées par l'IA"),
      text: t("L'IA locale reprend les faits de Generex dans le style d'une fiche de renseignement, sans rien ajouter."),
    },
  ];
  return (
    <EmptyState
      icon={Globe2}
      title={t("Fiche pays")}
      action={
        <Button
          data-attention={COUNTRY_WRITE_TARGET}
          highlight={complete}
          disabled={!known}
          blocked={known && !complete}
          onBlocked={onBlocked}
          onClick={onGenerate}
        >
          <Sparkles size={14} /> {t("Rédiger la fiche pays")}
        </Button>
      }
      secondary={
        known &&
        !complete && (
          <Button variant="link" data-attention={COMPLETE_TARGET} onClick={onComplete}>
            {t("Compléter les Données initiales")}
          </Button>
        )
      }
      note={aiAvailable === false ? t("IA hors ligne : texte de Generex gardé pour ses rubriques.") : t("Environ une minute, suivie en direct.")}
    >
      <p>{t("Le pays de l'ennemi joué, en deux ou trois pages, avec la mise en ambiance et l'OPO")}</p>
      {/* Liste alignée à gauche dans un encadré : un bloc ancré au milieu de l'état vide centré. */}
      <ul className="mt-4 space-y-3 rounded-lg border border-line bg-canvas/60 px-4 py-3 text-left">
        {items.map((it) => (
          <li key={it.title} className="flex gap-2.5">
            <Sparkles size={14} className="mt-0.5 shrink-0 text-run" aria-hidden />
            <span>
              <span className="block leading-snug font-medium text-ink">{it.title}</span>
              <span className="block text-meta leading-snug text-ink-soft">{it.text}</span>
            </span>
          </li>
        ))}
      </ul>
      <Presence show={Boolean(error)}>
        {error && (
          <Callout tone="danger" className="mt-4 text-left">
            {tServer(error)}
          </Callout>
        )}
      </Presence>
    </EmptyState>
  );
}
